import classNames from 'classnames';
import React, { forwardRef } from 'react';
import { NumberStepArrows } from './NumberStepArrows';

export const getInputClassName = ({ disabled, className }: { disabled?: boolean; className?: string }) => {
  return classNames(
    'text-black dark:text-white h-10 block rounded-md transition bg-white shadow-sm dark:bg-[#313131] dark:focus:ring-0',
    !className?.match(/\bw-/) && 'w-full',
    disabled && 'bg-[#A5ACB0] cursor-not-allowed',
    !className?.includes('border-error') && 'dark:border-gray-300 border-[#C2C6C8] dark:focus:border-neon-green-300',
    className,
  );
};

export type InputProps = React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> & {
  /** Number fields: a unit drawn right behind the value inside the field ("0.93 m"). */
  unit?: React.ReactNode;
};

/** Classes that place / size the field in its container: they go on a number field's wrapper, the rest on the input. */
const LAYOUT_CLASS = /^(?:[a-z]+:)*-?(?:w-|min-w-|max-w-|flex-|grow|shrink|basis-|self-|col-|row-|order-|m[trblxy]?-)/;

/** A number field: the input plus Blender-style hover step arrows (dragging it scrubs: utils/numberScrub.ts). */
const NumberInput = forwardRef<HTMLInputElement, InputProps>(({ className, disabled, unit, ...props }, ref) => {
  const classes = className?.split(/\s+/).filter(Boolean) ?? [];
  const outer = classes.filter((c) => LAYOUT_CLASS.test(c));
  const inner = classes.filter((c) => !LAYOUT_CLASS.test(c));
  // The unit follows the value (see .number-field-unit in index.css); nothing to follow while it is empty (mixed values).
  const shown = props.value ?? props.defaultValue;
  const withUnit = unit !== undefined && unit !== null && unit !== '' && shown !== undefined && String(shown) !== '';
  return (
    <span className={classNames('number-field block', withUnit && 'number-field-with-unit', outer.length > 0 ? outer : 'w-full')}>
      <input ref={ref} className={getInputClassName({ disabled, className: classNames(inner, 'w-full px-5') })} disabled={disabled} {...props} type="number" />
      {withUnit && (
        <span className="number-field-unit left-[21px] right-[21px] text-black dark:text-white">
          <span className="number-field-unit-value">{String(shown)}</span>
          <span className="ml-1 text-gray-500 dark:text-gray-400">{unit}</span>
        </span>
      )}
      {!disabled && !props.readOnly && <NumberStepArrows />}
    </span>
  );
});
NumberInput.displayName = 'NumberInput';

export const Input = forwardRef<HTMLInputElement, InputProps>(({ className, disabled, unit, ...props }, ref) => {
  if (props.type === 'number') return <NumberInput ref={ref} className={className} disabled={disabled} unit={unit} {...props} />;
  return <input ref={ref} type="text" className={getInputClassName({ disabled, className })} {...props} />;
});

Input.displayName = 'Input';
